import { TextField, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { alpha } from "@mui/material/styles";

const CustomTextField = ({ 
  label, 
  name, 
  value, 
  onChange, 
  onBlur, 
  type = "text",
  required = false,
  error = false,
  helperText = "",
  placeholder = "",
  disabled = false,
  multiline = false,
  rows = 1,
  maxLength,
  ...props 
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Determine the translation key based on the condition
  const translationKey = name && label && name.toLowerCase() === label.toLowerCase()
    ? name // Use `name` as the translation key if `label` and `name` (in lowercase) are equal
    : label; // Otherwise, use `label` as the translation key

  return (
    <TextField
      fullWidth
      label={t(translationKey)}
      type={type}
      name={name}
      value={value || ""}
      onChange={onChange}
      onBlur={onBlur}
      required={required}
      error={error}
      helperText={helperText}
      placeholder={placeholder}
      disabled={disabled}
      multiline={multiline}
      rows={rows}
      inputProps={{
        maxLength: maxLength,
        autoComplete: "off",
        ...props.inputProps,
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 1,
          transition: 'all 0.2s ease-in-out',
          backgroundColor: disabled ? theme.palette.action.disabledBackground : 'transparent',
          '&:hover': {
            backgroundColor: disabled ? theme.palette.action.disabledBackground : alpha(theme.palette.primary.main, 0.06),
          },
          '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: error ? theme.palette.error.main : theme.palette.divider,
          borderWidth: '1px',
          transition: 'all 0.2s ease-in-out',
        },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
          borderWidth: '2px',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
          borderWidth: '2px',
        },
        '& .MuiInputLabel-root': {
          color: error ? theme.palette.error.main : theme.palette.text.secondary,
          fontSize: '0.875rem',
          transform: 'translate(16px, 16px) scale(1)',
          '&.Mui-focused': {
            color: error ? theme.palette.error.main : theme.palette.primary.main,
            transform: 'translate(16px, -9px) scale(0.75)',
          },
          '&.MuiFormLabel-filled': {
            transform: 'translate(16px, -9px) scale(0.75)',
          },
        },
        '& .MuiFormHelperText-root': {
          marginLeft: 0,
          marginTop: 0.5,
          fontSize: '0.75rem',
        },
        '& .MuiInputBase-input': {
          padding: '16px 16px',
          fontSize: '0.875rem',
          lineHeight: 1.4,
          '&::placeholder': {
            color: theme.palette.text.disabled,
            opacity: 1,
            fontSize: '0.875rem',
            lineHeight: 1.4,
          },
        },
        '& .MuiInputBase-inputMultiline': {
          padding: '16px 16px',
          lineHeight: 1.4,
        },
        // Focus ring for accessibility
        '& .MuiOutlinedInput-root.Mui-focused': {
          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
        },
        // Error state focus ring
        '& .MuiOutlinedInput-root.Mui-error.Mui-focused': {
          boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.1)}`,
        },
        // Disabled state
        '& .MuiOutlinedInput-root.Mui-disabled': {
          backgroundColor: theme.palette.action.disabledBackground,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.action.disabled,
          },
        },
      }}
      {...props}
    />
  );
};

export default CustomTextField;